// Visit detail — shows the patient + service info and exposes the next-action
// button (Accept → Start → Submit Report). The valid SR transitions are
//   new → assigned → scheduled → in_progress → completed
// so "Start Visit" issues two PATCH calls in sequence.

import { useState, type JSX } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation, type UseMutationResult } from "@tanstack/react-query";
import {
  carerApi, SERVICE_LABELS, STATUS_CONFIG,
  type Visit, type Medication, type VisitReport,
  CarerApiError,
} from "../../lib/carerApi.js";
import { CarerNotificationBell } from "./CarerNotificationBell.js";

interface VisitDetailBundle {
  visit: Visit;
  meds: Medication[];
  report: VisitReport | null;
}

type NextAction = { kind: "accept" | "start" | "report"; label: string; color: string };

const STATUS_NEXT: Record<string, NextAction> = {
  new:         { kind: "accept", label: "Accept Visit",   color: "bg-navy-700 hover:bg-navy-800"     },
  assigned:    { kind: "start",  label: "Start Visit",    color: "bg-blue-600 hover:bg-blue-700"     },
  scheduled:   { kind: "start",  label: "Start Visit",    color: "bg-blue-600 hover:bg-blue-700"     },
  in_progress: { kind: "report", label: "Submit Report",  color: "bg-indigo-600 hover:bg-indigo-700" },
};

const STATUS_FALLBACK = { label: "New", color: "text-amber-700", bg: "bg-amber-50 border-amber-200" };

function patientLabelFor(visit: Visit): string {
  if (visit.patient_name && visit.patient_name.trim().length > 0) return visit.patient_name;
  return `Patient …${visit.patient_id.slice(-6)}`;
}

async function loadBundle(id: string): Promise<VisitDetailBundle> {
  const visit = await carerApi.getVisit(id);
  const [report, meds] = await Promise.all([
    carerApi.getVisitReport(id),
    visit.patient_id ? carerApi.getMedications(visit.patient_id) : Promise.resolve([]),
  ]);
  return { visit, meds, report };
}

function LoadingView(): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-navy-700 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
    </div>
  );
}

function NotFoundView({ onRetry, onBack }: { onRetry: () => void; onBack: () => void }): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="text-center">
        <p className="text-red-600">Visit not found.</p>
        <button onClick={onRetry} className="text-navy-700 mt-2 hover:underline">
          Retry
        </button>
        <button onClick={onBack} className="ml-4 text-navy-700 mt-2 hover:underline">
          ← Back to visits
        </button>
      </div>
    </div>
  );
}

function DetailHeader({ visit, onBack }: { visit: Visit; onBack: () => void }): JSX.Element {
  const status = STATUS_CONFIG[visit.status] ?? STATUS_CONFIG["new"] ?? STATUS_FALLBACK;
  return (
    <header
      className="bg-navy-800 text-white sticky top-0 z-10 shadow-md"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
        <button
          onClick={onBack}
          className="text-navy-200 hover:text-white text-2xl leading-none"
          aria-label="Back"
        >
          ←
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-lg truncate">Visit Detail</h1>
          <p className="text-navy-200 text-xs">
            {SERVICE_LABELS[visit.service_type] ?? visit.service_type}
          </p>
        </div>
        <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border ${status.bg} ${status.color}`}>
          {status.label}
        </span>
        <CarerNotificationBell />
      </div>
    </header>
  );
}

function PatientSection({ visit, patientLabel }: { visit: Visit; patientLabel: string }): JSX.Element {
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Patient</p>
      <p className="text-lg font-bold text-slate-900">{patientLabel}</p>
      {visit.address && <p className="text-slate-600 text-sm mt-1">📍 {visit.address}</p>}
      <p className="text-slate-600 text-sm mt-1">
        📅 {new Date(visit.scheduled_datetime).toLocaleString("en-IN", {
          weekday: "short", day: "numeric", month: "short",
          hour: "2-digit", minute: "2-digit", hour12: true,
        })}
      </p>
      {(visit.special_instructions || visit.notes) && (
        <p className="text-slate-500 text-sm mt-2 italic">
          📝 {visit.special_instructions || visit.notes}
        </p>
      )}
    </section>
  );
}

function MedsSection({ meds }: { meds: Medication[] }): JSX.Element {
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Medications</p>
      <div className="space-y-2">
        {meds.map((med) => (
          <div key={med._id} className="flex items-start gap-3">
            <span className="text-xl shrink-0" role="img" aria-hidden="true">💊</span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">
                {med.drug_name ?? med.generic_name ?? "Medication"}
              </p>
              {(med.dosage_mg || med.frequency) && (
                <p className="text-xs text-slate-500">
                  {med.dosage_mg ? `${med.dosage_mg} ${med.dosage_unit ?? "mg"}` : ""}
                  {med.dosage_mg && med.frequency ? " · " : ""}
                  {med.frequency ?? ""}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ReportBanner(): JSX.Element {
  return (
    <section className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
      <span className="text-2xl shrink-0" role="img" aria-hidden="true">✅</span>
      <div>
        <p className="font-semibold text-green-800">Report submitted</p>
        <p className="text-green-700 text-sm">Your visit report has been received.</p>
      </div>
    </section>
  );
}

function ActionBar({
  nextAction, isPending, onClick,
}: { nextAction: NextAction; isPending: boolean; onClick: () => void }): JSX.Element {
  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4"
      style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
    >
      <div className="max-w-lg mx-auto">
        <button
          onClick={onClick}
          disabled={isPending}
          className={`w-full py-4 px-6 rounded-2xl text-white font-bold text-base
                      disabled:opacity-50 transition-all active:scale-[0.98] shadow-lg ${nextAction.color}`}
        >
          {isPending ? "Please wait…" : nextAction.label}
        </button>
      </div>
    </div>
  );
}

export function CarerVisitDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [actError, setActError] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery<VisitDetailBundle>({
    queryKey: ["carer-visit", id],
    queryFn:  () => loadBundle(id ?? ""),
    enabled:  Boolean(id),
  });

  // Carer cannot self-assign in the API — but if we land on status="new"
  // we can transition directly to "assigned" via /sr/:id/status, mirroring
  // the mobile app's "Accept Visit" button.
  const transition: UseMutationResult<Visit, Error, "assigned" | "in_progress"> = useMutation({
    mutationFn: async (target) => {
      if (!id) throw new Error("No id");
      if (target === "in_progress") {
        // SR state machine requires scheduled before in_progress.
        if (data?.visit.status === "assigned") {
          await carerApi.updateVisitStatus(id, "scheduled");
        }
        return carerApi.updateVisitStatus(id, "in_progress");
      }
      return carerApi.updateVisitStatus(id, target);
    },
    onSuccess: async () => {
      setActError(null);
      await qc.invalidateQueries({ queryKey: ["carer-visit", id] });
      await qc.invalidateQueries({ queryKey: ["carer-visits"] });
    },
    onError: (err) => {
      const msg = err instanceof CarerApiError ? err.message : "Could not update visit.";
      setActError(msg);
    },
  });

  if (isLoading) return <LoadingView />;
  if (isError || !data) {
    return <NotFoundView onRetry={() => { void refetch(); }} onBack={() => navigate("/carer")} />;
  }

  const { visit, meds, report } = data;
  const nextAction = STATUS_NEXT[visit.status];
  const patientLabel = patientLabelFor(visit);

  function handleNextAction(): void {
    if (!nextAction || !id) return;
    if (nextAction.kind === "report") return navigate(`/carer/visit/${id}/report`);
    const target = nextAction.kind === "accept" ? "assigned" : "in_progress";
    transition.mutate(target);
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <DetailHeader visit={visit} onBack={() => navigate("/carer")} />

      <main className="max-w-lg mx-auto px-4 py-4 space-y-4 pb-32">
        <PatientSection visit={visit} patientLabel={patientLabel} />
        {meds.length > 0 && <MedsSection meds={meds} />}
        {report && <ReportBanner />}
        {actError && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <p className="text-red-700 text-sm">{actError}</p>
          </div>
        )}
      </main>

      {nextAction && !report && (
        <ActionBar nextAction={nextAction} isPending={transition.isPending} onClick={handleNextAction} />
      )}
    </div>
  );
}
